import type { AppPageKey } from "../app/router/AppRouter";

interface DashboardPageProps {
  onNavigate: (page: AppPageKey) => void;
}

const technologies = [
  "React",
  "TypeScript",
  "Three.js",
  "WebGL",
  "Microsoft Azure",
  "AI-assisted workflows",
  "REST APIs",
  "Enterprise architecture",
];

export function DashboardPage({ onNavigate }: DashboardPageProps) {
  return (
    <div className="page showcase-page">
      <section className="showcase-hero">
        <div className="showcase-hero__content">
          <div className="showcase-eyebrow">SALMAN ABEED · INNOVATION SHOWCASE</div>
          <h1>Engineering the Future of Interactive Learning</h1>
          <p className="showcase-hero__lead">
            A collection of working concepts exploring interactive 3D visualization,
            artificial intelligence, cloud computing, and modern software engineering.
          </p>
          <p className="showcase-hero__supporting">
            These prototypes demonstrate how technology can make complex professional
            knowledge easier to explore, understand, and apply.
          </p>
          <div className="showcase-actions">
            <button
              className="showcase-button showcase-button--primary"
              onClick={() => onNavigate("arch-designer")}
            >
              Explore the DCube Prototype
            </button>
            <a
              className="showcase-button showcase-button--secondary"
              href="https://www.linkedin.com/in/salman-abeed-12ba3323"
              target="_blank"
              rel="noreferrer"
            >
              View LinkedIn Profile
            </a>
          </div>
        </div>

        <div className="showcase-hero__visual" aria-label="Interactive 3D learning concept">
          <div className="showcase-orbit showcase-orbit--outer" />
          <div className="showcase-orbit showcase-orbit--inner" />
          <div className="showcase-cube">
            <span>3D</span>
          </div>
          <div className="showcase-float showcase-float--ai">AI</div>
          <div className="showcase-float showcase-float--cloud">Cloud</div>
          <div className="showcase-float showcase-float--learn">Learn</div>
        </div>
      </section>

      <section className="showcase-section showcase-intro">
        <div>
          <div className="showcase-section__kicker">ABOUT THE AUTHOR</div>
          <h2>Building at the intersection of engineering and learning</h2>
        </div>
        <div className="showcase-intro__copy">
          <p>
            I am <strong>Salman Abeed</strong>, a Software Development Manager and
            Enterprise Solutions Architect with more than 23 years of experience
            developing CAD, engineering, visualization, manufacturing, and enterprise
            software.
          </p>
          <p>
            My work has included leading global development teams at Bentley Systems,
            architecting enterprise applications, and applying cloud, AI, and interactive
            3D technologies to real-world workflows. This site brings those interests
            together through practical, working prototypes.
          </p>
        </div>
      </section>

      <section className="showcase-section">
        <div className="showcase-section__header">
          <div>
            <div className="showcase-section__kicker">FEATURED PROJECT</div>
            <h2>DCube: Interactive 3D learning for digital dentistry</h2>
          </div>
          <button
            className="showcase-text-link"
            onClick={() => onNavigate("arch-designer")}
          >
            Open prototype →
          </button>
        </div>

        <div className="featured-project">
          <div className="featured-project__summary">
            <p>
              DCube explores how interactive 3D visualization, guided workflows, and
              intelligent assistance can support education, collaboration, and restoration
              planning in digital dentistry.
            </p>
            <div className="featured-project__tags">
              <span>Interactive 3D</span>
              <span>Digital Dentistry</span>
              <span>Learning Technology</span>
              <span>Design Automation</span>
            </div>
          </div>

          <div className="workflow-mini" aria-label="DCube workflow">
            <div><strong>01</strong><span>Clinical intent</span></div>
            <div><strong>02</strong><span>3D anatomy</span></div>
            <div className="workflow-mini__active"><strong>03</strong><span>Interactive design</span></div>
            <div><strong>04</strong><span>Production output</span></div>
          </div>
        </div>
      </section>

      <section className="showcase-section showcase-story">
        <div className="showcase-story__number">23+</div>
        <div>
          <div className="showcase-section__kicker">FROM CAD TO DIGITAL DENTISTRY</div>
          <h2>Transferring proven engineering ideas into new domains</h2>
          <p>
            For more than two decades, I have worked on software used to design complex
            industrial facilities and engineering systems. The same foundations—3D
            modeling, geometric reasoning, visualization, user interaction, data
            management, and design automation—also underpin modern digital dentistry.
            DCube demonstrates how that experience can be applied to create engaging
            learning experiences and next-generation professional tools.
          </p>
        </div>
      </section>

      <section className="showcase-section">
        <div className="showcase-section__kicker">TECHNOLOGIES DEMONSTRATED</div>
        <h2>A modern platform built across disciplines</h2>
        <div className="technology-grid">
          {technologies.map((technology, index) => (
            <div className="technology-card" key={technology}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{technology}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="showcase-section showcase-vision">
        <div>
          <div className="showcase-section__kicker">LOOKING AHEAD</div>
          <h2>Beyond static content</h2>
        </div>
        <p>
          Future concepts may include guided learning pathways, interactive clinical
          cases, collaborative sessions, AI-assisted tutoring, simulation, research
          visualization, and integration with learning management systems.
        </p>
      </section>

      <section className="showcase-footer-card">
        <div>
          <strong>Designed and developed by Salman Abeed</strong>
          <span>Software Development Manager · Enterprise Solutions Architect</span>
        </div>
        <a
          href="https://www.linkedin.com/in/salman-abeed-12ba3323"
          target="_blank"
          rel="noreferrer"
        >
          Connect on LinkedIn →
        </a>
      </section>
    </div>
  );
}
